import "./PortfolioManagement.css"; // Styles for this component

import { Category, allCategoriesData } from "../../data/categories";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

type RiskLevel = "Conservative" | "Balanced" | "Aggressive";

interface RiskProfile {
  expectedReturn: number;
  maxDrawdown: number;
}

const riskProfiles: Record<RiskLevel, RiskProfile> = {
  Conservative: { expectedReturn: 30, maxDrawdown: 20 },
  Balanced: { expectedReturn: 50, maxDrawdown: 40 },
  Aggressive: { expectedReturn: 300, maxDrawdown: 70 },
};

// Define the portfolio form data interface
export interface PortfolioFormData {
  investmentAmount: number;
  riskLevel: string;
  expectedReturn: number;
  maxDrawdown: number;
  investmentPeriod: number;
  inclusions: string[];
  exclusions: string[];
}

// Function to generate template string from form data
const generateTemplateString = (data: PortfolioFormData): string => {
  // Convert inclusions and exclusions arrays to comma-separated strings
  const inclusionsStr = data.inclusions.join(", ");
  const exclusionsStr = data.exclusions.join(", ");

  const lines = [
    "",
    "  I want to create a personalized investment portfolio based on the",
    "",
    "  following parameters:",
    "",
    `  - Investment Amount: ${data.investmentAmount} USD`,
    "",
    `  - Risk Level: ${data.riskLevel}`,
    "",
    `  - Expected Return: ${data.expectedReturn}%`,
    "",
    `  - Max Drawdown: ${data.maxDrawdown}%`,
    "",
    `  - Investment Period: ${data.investmentPeriod} months`,
  ];

  if (inclusionsStr) {
    lines.push("");
    lines.push(`  - Specific Inclusions: ${inclusionsStr}`);
  }

  if (exclusionsStr) {
    lines.push("");
    lines.push(`  - Specific Exclusions: ${exclusionsStr}`);
  }

  lines.push("");

  lines.push(
    "  Please provide portfolio recommendations based on these parameters,"
  );
  lines.push("");
  lines.push(
    "  including asset allocation, suggested investment actions, and potential"
  );
  lines.push("");
  lines.push("  risk explanations.");
  return lines.join("\r\n");
};

// Extract header content from Header.tsx
export const getHeaderContent = () => {
  return {
    iconSrc: "/portfolio_management_icon.png",
    iconAlt: "Portfolio Icon",
    title: "Portfolio Management",
    subtitle:
      "Get the latest updates, news, and price information\nSelect a project you're interested in, and we'll provide you with the latest information",
  };
};

export interface PortfolioManagementHandle {
  getFormData: () => PortfolioFormData | null;
  generateTemplate: () => string | null;
}

interface PortfolioManagementProps {
  onValidityChange?: (isValid: boolean) => void;
}

const PortfolioManagement = forwardRef<
  PortfolioManagementHandle,
  PortfolioManagementProps
>(({ onValidityChange }, ref) => {
  const [investmentAmount, setInvestmentAmount] = useState<string>("");
  const [investmentAmountError, setInvestmentAmountError] =
    useState<string>("");
  const [selectedRiskLevel, setSelectedRiskLevel] =
    useState<RiskLevel>("Conservative");
  const [expectedReturn, setExpectedReturn] = useState<number>(
    riskProfiles.Conservative.expectedReturn
  );
  const [maxDrawdown, setMaxDrawdown] = useState<number>(
    riskProfiles.Conservative.maxDrawdown
  );
  const [selectedInvestmentPeriod, setSelectedInvestmentPeriod] =
    useState<number>(3); // Default to 3 months

  const [inclusions, setInclusions] = useState<string[]>([
    "layer-1",
    "ai",
    "defi",
  ]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [showAllInclusions, setShowAllInclusions] = useState(false);
  const [showAllExclusions, setShowAllExclusions] = useState(false);

  const parsedInvestmentAmount = parseFloat(investmentAmount);
  const isInvestmentAmountValid =
    !isNaN(parsedInvestmentAmount) && parsedInvestmentAmount >= 1000;

  const validateAndSetInvestmentAmount = (value: string) => {
    setInvestmentAmount(value);
    if (value === "") {
      setInvestmentAmountError("");
      return;
    }
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setInvestmentAmountError("Please enter a valid number.");
    } else if (numValue < 1000) {
      setInvestmentAmountError("Minimum investment is $1,000.");
    } else {
      setInvestmentAmountError("");
    }
  };

  const handleRiskLevelSelect = (level: RiskLevel) => {
    setSelectedRiskLevel(level);
    setExpectedReturn(riskProfiles[level].expectedReturn);
    setMaxDrawdown(riskProfiles[level].maxDrawdown);
  };

  const handleInvestmentPeriodChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedInvestmentPeriod(parseInt(event.target.value, 10));
  };

  const handleToggleInclusion = useCallback(
    (categoryId: string) => {
      setInclusions((prev) =>
        prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId]
      );
      // If adding to inclusions, remove from exclusions
      if (!inclusions.includes(categoryId)) {
        setExclusions((prev) => prev.filter((id) => id !== categoryId));
      }
    },
    [inclusions]
  );

  const handleToggleExclusion = useCallback(
    (categoryId: string) => {
      setExclusions((prev) =>
        prev.includes(categoryId)
          ? prev.filter((id) => id !== categoryId)
          : [...prev, categoryId]
      );
      // If adding to exclusions, remove from inclusions
      if (!exclusions.includes(categoryId)) {
        setInclusions((prev) => prev.filter((id) => id !== categoryId));
      }
    },
    [exclusions]
  );

  // Define the IDs of categories that are visible by default
  const defaultVisibleCategoryIds = [
    "ai",
    "defi",
    "gaming",
    "layer-1",
    "layer-2",
    "meme",
    "nft",
    "social",
  ];

  const renderCategorySection = (
    title: string,
    allAvailableCategories: Category[], // This will be allCategoriesData
    selectedCategories: string[],
    onToggleCategory: (id: string) => void,
    showAll: boolean,
    onToggleShowAll: () => void,
    sectionType: "inclusion" | "exclusion"
  ) => {
    // Separate categories into default visible and initially hidden
    const defaultVisibleItems = allAvailableCategories.filter((category) =>
      defaultVisibleCategoryIds.includes(category.id)
    );
    // Ensure other categories are in hiddenByDefaultItems, maintaining their original sort order from allAvailableCategories
    const hiddenByDefaultItems = allAvailableCategories.filter(
      (category) => !defaultVisibleCategoryIds.includes(category.id)
    );

    const visibleCategories = showAll
      ? [...defaultVisibleItems, ...hiddenByDefaultItems] // Show default first, then others, maintaining their relative order
      : defaultVisibleItems;

    return (
      <div className="form-section">
        <label className="form-label">{title}</label>
        <div className="category-tags">
          {visibleCategories.map((category) => (
            <button
              key={category.id}
              className={`category-tag ${
                selectedCategories.includes(category.id)
                  ? sectionType === "inclusion"
                    ? "inclusion-selected"
                    : "exclusion-selected"
                  : ""
              }`}
              onClick={() => onToggleCategory(category.id)}
            >
              {category.name}
            </button>
          ))}
        </div>
        {/* Only show 'View More' if there are categories that are hidden by default */}
        {hiddenByDefaultItems.length > 0 && (
          <button onClick={onToggleShowAll} className="view-more-button">
            {showAll ? "View Less" : "View More"}
          </button>
        )}
      </div>
    );
  };

  // Report validity change to parent
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isInvestmentAmountValid);
    }
  }, [isInvestmentAmountValid, onValidityChange]);

  // Expose form data and template generation to the parent component via ref
  useImperativeHandle(ref, () => ({
    getFormData: () => {
      if (isInvestmentAmountValid) {
        return {
          investmentAmount: parsedInvestmentAmount,
          riskLevel: selectedRiskLevel,
          expectedReturn: expectedReturn,
          maxDrawdown: maxDrawdown,
          investmentPeriod: selectedInvestmentPeriod,
          inclusions: inclusions,
          exclusions: exclusions,
        };
      }
      return null;
    },
    generateTemplate: () => {
      const formData = isInvestmentAmountValid
        ? {
            investmentAmount: parsedInvestmentAmount,
            riskLevel: selectedRiskLevel,
            expectedReturn: expectedReturn,
            maxDrawdown: maxDrawdown,
            investmentPeriod: selectedInvestmentPeriod,
            inclusions: inclusions,
            exclusions: exclusions,
          }
        : null;

      return formData ? generateTemplateString(formData) : null;
    },
  }));

  return (
    <main className="body-container">
      {/* Investment Amount Section */}
      <div className="form-section">
        <label className="form-label">Investment Amount (USD)</label>
        <div className="input-container">
          <span className="currency-symbol">$</span>
          <input
            type="text"
            value={investmentAmount}
            onChange={(e) => validateAndSetInvestmentAmount(e.target.value)}
            className="form-input"
            placeholder="Enter amount"
          />
        </div>
        {investmentAmountError && (
          <div className="error-message">{investmentAmountError}</div>
        )}
      </div>

      {/* Risk Level Section */}
      <div className="form-section">
        <label className="form-label">Risk Level</label>
        <div className="risk-level-options">
          {(["Conservative", "Balanced", "Aggressive"] as RiskLevel[]).map(
            (level) => (
              <div
                key={level}
                className={`risk-level-option ${
                  selectedRiskLevel === level ? "selected" : ""
                }`}
                onClick={() => handleRiskLevelSelect(level)}
              >
                <p className="risk-level-title">{level}</p>
                <p className="risk-level-description">
                  {level === "Conservative" &&
                    "Capital preservation, low risk, stable returns"}
                  {level === "Balanced" &&
                    "Balance between risk and return, moderate volatility"}
                  {level === "Aggressive" &&
                    "Higher returns, greater volatility tolerance"}
                </p>
              </div>
            )
          )}
        </div>
        <div className="stats-container">
          <div className="stat-item">
            <svg
              className="stat-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.4 21C6.88333 21 5.604 20.4793 4.562 19.438C3.52 18.3967 2.99933 17.1173 3 15.6C3 14.9667 3.10833 14.35 3.325 13.75C3.54167 13.15 3.85 12.6083 4.25 12.125L7.8 7.85L6.1 4.45C5.93333 4.11667 5.946 3.79167 6.138 3.475C6.33 3.15833 6.61733 3 7 3H17C17.3833 3 17.671 3.15833 17.863 3.475C18.055 3.79167 18.0673 4.11667 17.9 4.45L16.2 7.85L19.75 12.125C20.15 12.6083 20.4583 13.15 20.675 13.75C20.8917 14.35 21 14.9667 21 15.6C21 17.1167 20.475 18.396 19.425 19.438C18.375 20.48 17.1 21.0007 15.6 21H8.4ZM12 16C11.45 16 10.9793 15.8043 10.588 15.413C10.1967 15.0217 10.0007 14.5507 10 14C9.99933 13.4493 10.1953 12.9787 10.588 12.588C10.9807 12.1973 11.4513 12.0013 12 12C12.5487 11.9987 13.0197 12.1947 13.413 12.588C13.8063 12.9813 14.002 13.452 14 14C13.998 14.548 13.8023 15.019 13.413 15.413C13.0237 15.807 12.5527 16.0027 12 16ZM9.625 7H14.375L15.375 5H8.625L9.625 7ZM8.4 19H15.6C16.55 19 17.3543 18.671 18.013 18.013C18.6717 17.355 19.0007 16.5507 19 15.6C19 15.2 18.929 14.8127 18.787 14.438C18.645 14.0633 18.4493 13.7257 18.2 13.425L14.525 9H9.5L5.8 13.4C5.55 13.7 5.35433 14.0417 5.213 14.425C5.07167 14.8083 5.00067 15.2 5 15.6C5 16.55 5.32933 17.3543 5.988 18.013C6.64667 18.6717 7.45067 19.0007 8.4 19Z"
                fill="currentColor"
                fillOpacity="0.8"
              />
            </svg>
            <span className="stat-label">Expected Return:</span>
            <span className="stat-value positive">{expectedReturn}%</span>
          </div>
          <div className="divider"></div>
          <div className="stat-item">
            <svg
              className="stat-icon"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.875 12.0001V18.0001C22.875 18.2984 22.7564 18.5846 22.5455 18.7956C22.3345 19.0065 22.0483 19.1251 21.75 19.1251H15.75C15.4516 19.1251 15.1654 19.0065 14.9545 18.7956C14.7435 18.5846 14.625 18.2984 14.625 18.0001C14.625 17.7017 14.7435 17.4156 14.9545 17.2046C15.1654 16.9936 15.4516 16.8751 15.75 16.8751H19.0312L12.75 10.5938L9.7959 13.5488C9.69138 13.6537 9.56719 13.7369 9.43044 13.7937C9.2937 13.8505 9.14709 13.8797 8.99902 13.8797C8.85096 13.8797 8.70435 13.8505 8.5676 13.7937C8.43085 13.7369 8.30666 13.6537 8.20215 13.5488L1.45215 6.79883C1.2408 6.58748 1.12207 6.30084 1.12207 6.00195C1.12207 5.85396 1.15122 5.70741 1.20785 5.57069C1.26449 5.43396 1.3475 5.30972 1.45215 5.20508C1.55679 5.10043 1.68103 5.01742 1.81776 4.96078C1.95448 4.90415 2.10103 4.875 2.24902 4.875C2.54791 4.875 2.83455 4.99373 3.0459 5.20508L8.99996 11.1563L11.954 8.20133C12.0585 8.09645 12.1827 8.01323 12.3195 7.95645C12.4562 7.89967 12.6028 7.87044 12.7509 7.87044C12.899 7.87044 13.0456 7.89967 13.1823 7.95645C13.3191 8.01323 13.4433 8.09645 13.5478 8.20133L20.625 15.2813V12.0001C20.625 11.7017 20.7435 11.4156 20.9545 11.2046C21.1654 10.9936 21.4516 10.8751 21.75 10.8751C22.0483 10.8751 22.3345 10.9936 22.5455 11.2046C22.7564 11.4156 22.875 11.7017 22.875 12.0001Z"
                fill="currentColor"
                fillOpacity="0.8"
              />
            </svg>
            <span className="stat-label">Max Drawdown:</span>
            <span className="stat-value negative">{maxDrawdown}%</span>
          </div>
        </div>
      </div>

      {/* Investment Period Section */}
      <div className="form-section">
        <label className="form-label">Investment Period (months)</label>
        <select
          value={selectedInvestmentPeriod}
          onChange={handleInvestmentPeriodChange}
          className="form-select"
        >
          <option value="3">3 months</option>
          <option value="6">6 months</option>
          <option value="12">12 months</option>
        </select>
      </div>

      {/* Inclusion Categories Section */}
      {renderCategorySection(
        "Inclusion Categories",
        allCategoriesData,
        inclusions,
        handleToggleInclusion,
        showAllInclusions,
        () => setShowAllInclusions(!showAllInclusions),
        "inclusion"
      )}

      {/* Exclusion Categories Section */}
      {/* test */}
      {renderCategorySection(
        "Exclusion Categories",
        allCategoriesData,
        exclusions,
        handleToggleExclusion,
        showAllExclusions,
        () => setShowAllExclusions(!showAllExclusions),
        "exclusion"
      )}
    </main>
  );
});

PortfolioManagement.displayName = "PortfolioManagement"; // Added display name for linting/debugging

export default PortfolioManagement;
